import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import { DictateButton, DictationOverlay } from "../../dictation";
import { dictateListing, redictateText, reportDictationOutcome } from "../services/dictation.js";
import listingDictationSchema from "../constants/listingDictation.js";
import DictationReview from "./DictationReview.jsx";

/**
 * The listing-specific wiring around the shared dictation overlay: the schema,
 * the editable review panel, and what happens once a listing exists.
 *
 * The listing is created from inside the sheet — the rep no longer passes
 * through the create form. Nothing navigates on its own: the sheet's success
 * state offers View listing and the rep decides.
 */

const ListingDictation = ({ disabled = false, title }) => {
  const history = useHistory();
  const [open, setOpen] = useState(false);

  const upload = (blob, { signal }) => dictateListing(blob, { signal });

  // Feeds the score-floor calibration, which ships uncalibrated — a dismissal is
  // as informative as an acceptance. Fire-and-forget: never blocks anything.
  const dismissed = (result) => {
    if (result?.usageId) reportDictationOutcome(result.usageId, { accepted: false });
  };

  const created = (result, onDone) => (listing, candidate, position) => {
    reportDictationOutcome(result?.usageId, {
      accepted: true,
      productId: candidate.id,
      position,
    });
    // Collapses the sheet down to the success state.
    onDone();
  };

  const viewListing = (listing) => {
    setOpen(false);
    history.push(`/product/${listing.id}`);
  };

  const searchManually = (result) => {
    dismissed(result);
    setOpen(false);
    history.push({ pathname: "/product/create" });
  };

  return (
    <>
      <DictateButton
        onClick={() => setOpen(true)}
        disabled={disabled}
        {...(title ? { title } : {})}
      />
      {open && (
        <DictationOverlay
          isOpen={open}
          onClose={(result) => {
            dismissed(result);
            setOpen(false);
          }}
          schema={listingDictationSchema}
          upload={upload}
          // The correction path: a fixed transcript goes back through the same
          // endpoint as `text`, so the fields, candidates and missing list are
          // all re-derived from what the rep actually meant.
          reanalyse={(text) => redictateText(text)}
          renderReview={(result, { onDone }) => (
            <DictationReview
              result={result}
              onCreated={created(result, onDone)}
              onViewListing={viewListing}
              onSearchManually={searchManually}
            />
          )}
          onSearchManually={searchManually}
        />
      )}
    </>
  );
};

export default ListingDictation;
